import { rtkApi } from '@/apis/rtkApi';

export type ClauseDictionaryEntry = {
  _id: string;
  clauseId: string;
  title: string;
  description?: string;
  category?: string;
  subcategory?: string;
  jurisdictions?: string[];
  propertyTypes?: string[];
  requiredByState?: boolean;
  isNegotiable?: boolean;
  isRequired?: boolean;
  defaultValue?: unknown;
  validation?: {
    type?: string;
    required?: boolean;
    options?: string[];
  };
  contractFieldPath?: string;
  sortOrder?: number;
  status?: 'active' | 'proposed' | 'archived';
  authoredByRole?: string;
  contractId?: string | null;
  active?: boolean;
};

export const clauseDictionaryApi = rtkApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    resolveClauseDictionary: builder.query<
      ClauseDictionaryEntry[],
      { state?: string; propertyType?: string; contractId?: string }
    >({
      query: (params) => ({
        url: '/clause-dictionary/resolve',
        method: 'GET',
        params,
      }),
      transformResponse: (r: {
        success: boolean;
        data: ClauseDictionaryEntry[];
      }) => r.data ?? [],
      providesTags: ['ClauseDictionary'],
    }),

    proposeClauseDictionary: builder.mutation<
      ClauseDictionaryEntry,
      {
        contractId: string;
        title: string;
        description?: string;
        defaultValue?: unknown;
        jurisdictions?: string[];
        propertyTypes?: string[];
      }
    >({
      query: (body) => ({
        url: '/clause-dictionary/propose',
        method: 'POST',
        data: body,
      }),
      transformResponse: (r: {
        success: boolean;
        data: ClauseDictionaryEntry;
      }) => r.data,
      invalidatesTags: ['ClauseDictionary'],
    }),

    getAdminClauseDictionary: builder.query<ClauseDictionaryEntry[], void>({
      query: () => ({
        url: '/admin/clause-dictionary',
        method: 'GET',
      }),
      transformResponse: (r: {
        success: boolean;
        data: ClauseDictionaryEntry[];
      }) => r.data ?? [],
      providesTags: ['ClauseDictionary'],
    }),

    createAdminClauseDictionary: builder.mutation<
      ClauseDictionaryEntry,
      Partial<ClauseDictionaryEntry>
    >({
      query: (body) => ({
        url: '/admin/clause-dictionary',
        method: 'POST',
        data: body,
      }),
      transformResponse: (r: {
        success: boolean;
        data: ClauseDictionaryEntry;
      }) => r.data,
      invalidatesTags: ['ClauseDictionary'],
    }),

    updateAdminClauseDictionary: builder.mutation<
      ClauseDictionaryEntry,
      { id: string; body: Partial<ClauseDictionaryEntry> }
    >({
      query: ({ id, body }) => ({
        url: `/admin/clause-dictionary/${id}`,
        method: 'PUT',
        data: body,
      }),
      transformResponse: (r: {
        success: boolean;
        data: ClauseDictionaryEntry;
      }) => r.data,
      invalidatesTags: ['ClauseDictionary'],
    }),

    approveAdminClauseDictionary: builder.mutation<
      ClauseDictionaryEntry,
      string
    >({
      query: (id) => ({
        url: `/admin/clause-dictionary/${id}/approve`,
        method: 'POST',
      }),
      transformResponse: (r: {
        success: boolean;
        data: ClauseDictionaryEntry;
      }) => r.data,
      invalidatesTags: ['ClauseDictionary'],
    }),

    archiveAdminClauseDictionary: builder.mutation<
      ClauseDictionaryEntry,
      string
    >({
      query: (id) => ({
        url: `/admin/clause-dictionary/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (r: {
        success: boolean;
        data: ClauseDictionaryEntry;
      }) => r.data,
      invalidatesTags: ['ClauseDictionary'],
    }),

    ensureAdminClauseDictionarySeed: builder.mutation<
      ClauseDictionaryEntry[],
      void
    >({
      query: () => ({
        url: '/admin/clause-dictionary/ensure-seed',
        method: 'POST',
      }),
      transformResponse: (r: {
        success: boolean;
        data: ClauseDictionaryEntry[];
      }) => r.data ?? [],
      invalidatesTags: ['ClauseDictionary'],
    }),
  }),
});

export const {
  useResolveClauseDictionaryQuery,
  useProposeClauseDictionaryMutation,
  useGetAdminClauseDictionaryQuery,
  useCreateAdminClauseDictionaryMutation,
  useUpdateAdminClauseDictionaryMutation,
  useApproveAdminClauseDictionaryMutation,
  useArchiveAdminClauseDictionaryMutation,
  useEnsureAdminClauseDictionarySeedMutation,
} = clauseDictionaryApi;
